import { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaBuilding } from 'react-icons/fa';
import api from '../../api';
import { useColumnVisibility, ColumnPicker } from '../../components/ColumnPicker';

const empty = {
  name: '', address1: '', city: '', postalCode: '', email: '',
  isActive: true, earlyPaymentOption: false, discountPerHour: 0, notes: '',
};

export default function AdminClients() {
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]         = useState(empty);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.getClients()
      .then(setClients)
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const openNew = () => { setForm(empty); setEditingId(null); setShowForm(true); setError(''); };
  const openEdit = (c) => {
    setForm({
      name: c.name, address1: c.address1 || '', city: c.city || '',
      postalCode: c.postal_code || '', email: c.email || '',
      isActive: c.is_active, earlyPaymentOption: c.early_payment_option,
      discountPerHour: c.discount_per_hour || 0, notes: c.notes || '',
    });
    setEditingId(c.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      if (editingId) {
        await api.updateClient(editingId, form);
        setSuccess('Client updated.');
      } else {
        await api.createClient(form);
        setSuccess('Client created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteClient(id);
      setSuccess('Client deleted.');
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const COLS = [
    { key: 'city',     label: 'City'        },
    { key: 'email',    label: 'Email'       },
    { key: 'earlyPay', label: 'Early Pay'   },
    { key: 'discount', label: 'Discount/hr' },
    { key: 'active',   label: 'Active'      },
  ];
  const { visible, toggle, show } = useColumnVisibility(COLS, 'cols_clients');

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0c1b4d] flex items-center justify-center">
              <FaBuilding className="text-[#D4AF37]" size={16} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0c1b4d]">Clients</h1>
              <p className="text-xs text-gray-500">{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors"
          >
            <FaPlus size={12} /> Add Client
          </button>
        </div>

        {error   && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        {success && <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>}

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No clients yet. Add one above.</div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ColumnPicker columns={COLS} visible={visible} onToggle={toggle} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Name</th>
                    {show('city')     && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">City</th>}
                    {show('email')    && <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Email</th>}
                    {show('earlyPay') && <th className="px-4 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Early Pay</th>}
                    {show('discount') && <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Discount/hr</th>}
                    {show('active')   && <th className="px-4 py-3 text-center font-semibold text-gray-600 whitespace-nowrap">Active</th>}
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clients.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{c.name}</td>
                      {show('city')     && <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.city || '—'}</td>}
                      {show('email')    && <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.email || '—'}</td>}
                      {show('earlyPay') && <td className="px-4 py-3 text-center">
                        {c.early_payment_option
                          ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Yes</span>
                          : <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">No</span>}
                      </td>}
                      {show('discount') && <td className="px-4 py-3 text-right text-gray-600">
                        {c.early_payment_option ? `$${Number(c.discount_per_hour).toFixed(2)}` : '—'}
                      </td>}
                      {show('active') && <td className="px-4 py-3 text-center">
                        {c.is_active
                          ? <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Active</span>
                          : <span className="px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full text-xs">Inactive</span>}
                      </td>}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit"><FaEdit size={13} /></button>
                          <button onClick={() => setConfirmDelete(c)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete"><FaTrash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-[#0c1b4d]">{editingId ? 'Edit Client' : 'Add Client'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FaTimes size={14} /></button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Client Name *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
                  <input value={form.address1} onChange={e => set('address1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">City</label>
                  <input value={form.city} onChange={e => set('city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Postal Code</label>
                  <input value={form.postalCode} onChange={e => set('postalCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0c1b4d] focus:ring-[#0c1b4d]" />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={form.earlyPaymentOption} onChange={e => set('earlyPaymentOption', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#0c1b4d] focus:ring-[#0c1b4d]" />
                  Early Payment Option
                </label>
              </div>

              {form.earlyPaymentOption && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Discount per Hour ($)</label>
                  <input type="number" min="0" step="0.01" value={form.discountPerHour}
                    onChange={e => set('discountPerHour', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]" />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d] resize-none" />
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0c1b4d] text-white rounded-lg text-sm font-semibold hover:bg-blue-900 transition-colors disabled:opacity-60">
                  <FaSave size={13} /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm px-6 py-5">
            <h3 className="text-base font-bold text-gray-800 mb-2">Delete Client?</h3>
            <p className="text-sm text-gray-600 mb-5">Are you sure you want to delete <strong>{confirmDelete.name}</strong>? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
